import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import { compact, differenceBy, isEmpty } from 'lodash';
import claimTypeDealerAutomationRuleDeleteApi from 'models/dealerManagement/claimTypeDealerAutomationRule/delete/Api.ClaimTypeDealerAutomationRule.Delete';
import claimTypeDealerAutomationRuleSaveApi from 'models/dealerManagement/claimTypeDealerAutomationRule/save/Api.ClaimTypeDealerAutomationRule.Save';
import dealerDeleteApi from 'models/dealerManagement/dealer/delete/Api.Dealer.Delete';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import dealerClaimTypeDeleteApi from 'models/dealerManagement/dealerClaimType/delete/Api.DealerClaimType.Delete';
import dealerClaimTypeSaveApi from 'models/dealerManagement/dealerClaimType/save/Api.DealerClaimType.Save';
import { useMemo } from 'react';
import { dealerIndexRoute } from './Dealer.Index';

export enum DealerAction {
  EDIT = 'edit',
  DELETE = 'delete',
}
export type VerifyDealerAction = Utils.VerifyExtends<Module.Claims.Dealer.Actions, Utils.ValueOf<typeof DealerAction>>;
export type DealerForm = Omit<Api.Core.Dealer.Save.IRequest, 'productDealerDocuments'> & {
  files: Hooks.Input.File.Info[];
  logo: Hooks.Input.File.Info;
  productDealerDocuments: Array<
    Utils.Unpacked<Api.Core.Dealer.Save.IRequest['productDealerDocuments']> & {
      formFile?: Hooks.Input.File.Info;
      isDeleted?: boolean;
    }
  >;
};
export default function useDealer() {
  const { permissions, user } = useAuthStore();
  const pageRouter = usePageRouter<Module.Claims.Dealer.Params, Module.Claims.Dealer.Query>({
    route: dealerIndexRoute,
  });

  const canView = permissions?.CREATE_DEALER || permissions?.EDIT_DEALER || permissions?.DELETE_DEALER;
  const listApi = useApi(
    {
      action: Dealer.list,
      default: { limit: defaultLimit, offset: defaultOffset },
      body: {
        ...pageRouter.query.list,
        distributorId: compact([user?.distributorId]),
      },
      callback: (req) => pageRouter.redirect({}, { ...pageRouter.query, list: req }),
      wait: !canView || !user,
    },
    [canView, user]
  );

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const deleteApi = useApi({
    action: dealerDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const saveDealerAutomationRules = async (
    claimTypeDealerAutomationRules: Utils.FormData<Model.IClaimTypeDealerAutomationRule[]>,
    dealerClaimTypes: Utils.FormData<Model.IDealerClaimType[]>,
    claimTypes: Model.IClaimType[],
    dealerId: number
  ) => {
    for (const claimType of claimTypes ?? []) {
      const removed = differenceBy(claimType?.claimTypeAutomationRules ?? [], dealerClaimTypes ?? [], 'claimTypeId');
      for (const removedItem of removed) {
        const dga = selected?.claimTypeDealerAutomationRules?.find(
          (it) => it.claimTypeAutomationRuleId === removedItem?.id
        );
        if (dga && dga.id) {
          await claimTypeDealerAutomationRuleDeleteApi({ id: dga?.id });
        }
      }
    }
    for (const claimTypeDealerAutomationRule of claimTypeDealerAutomationRules || []) {
      const payload = {
        ...claimTypeDealerAutomationRule,
        claimTypeAutomationRuleId: claimTypeDealerAutomationRule.claimTypeAutomationRuleId,
        effects: claimTypeDealerAutomationRule.effects,
        conditions: claimTypeDealerAutomationRule.conditions,
        claimTypeId:
          claimTypeDealerAutomationRule?.claimTypeId ??
          claimTypeDealerAutomationRule?.claimTypeAutomationRule?.claimTypeId,
        dealerId,
      };
      await claimTypeDealerAutomationRuleSaveApi(
        { ...payload },
        {
          claimTypeId: payload.claimTypeId,
        }
      );
    }
  };
  const saveDealerClaimTypes = async (dealerClaimTypes: Utils.FormData<Model.IDealerClaimType[]>) => {
    const removed = differenceBy(selected?.dealerClaimTypes || [], dealerClaimTypes, 'claimTypeId');
    for (const removedItem of removed) {
      if (removedItem.id) {
        await dealerClaimTypeDeleteApi({ id: removedItem.id });
      }
    }
    if (!isEmpty(dealerClaimTypes)) {
      for (const dealerClaimType of dealerClaimTypes) {
        await dealerClaimTypeSaveApi(dealerClaimType);
      }
    }
  };

  return {
    canView,
    pageRouter,
    listApi,
    deleteApi,
    selected,
    permissions,
    saveDealerClaimTypes,
    saveDealerAutomationRules,
    views: {
      [DealerAction.DELETE]: pageRouter.params.action === DealerAction.DELETE && selected && permissions?.DELETE_DEALER,
      [DealerAction.EDIT]:
        pageRouter.params.action === DealerAction.EDIT &&
        ((permissions?.CREATE_DEALER && !selected) || (permissions?.EDIT_DEALER && selected)),
    },
  };
}

export function useDealerProvider() {
  return useDataProvider<ReturnType<typeof useDealer>>();
}
